<template>
    <div class="editable-products-container glass-theme">
        <!-- Enhanced Loading Overlay -->
        <div v-if="isPageLoading" class="loading-overlay">
            <div class="loading-card">
                <div class="loading-animation">
                    <div class="loading-ring">
                        <div class="ring"></div>
                        <div class="ring"></div>
                        <div class="ring"></div>
                        <div class="loading-logo">
                            <span class="loading-icon">📦</span>
                        </div>
                    </div>
                </div>
                <div class="loading-text">
                    <h3>{{ loadingMessage }}</h3>
                    <div class="loading-dots">
                        <span class="dot"></span>
                        <span class="dot"></span>
                        <span class="dot"></span>
                    </div>
                </div>
                <div class="loading-progress">
                    <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
                </div>
                <div class="loading-tips">
                    <p class="tip-text">{{ currentTip }}</p>
                </div>
            </div>
        </div>

        <!-- Original Loading State -->
        <div v-else-if="items_being_loaded" class="loading-container">
            <div class="loading-animation">
                <div class="loading-ring">
                    <div></div><div></div><div></div><div></div>
                </div>
                <div class="loading-text">Loading Products...</div>
                <div class="loading-dots">
                    <span></span><span></span><span></span>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div v-else class="products-wrapper glass-card">
            <!-- Header Controls -->
            <div class="header-controls">
                <div class="controls-left">
                    <b-button variant="success" @click="fetch_init(false, true)" class="fetch-btn glass-btn">
                        <b-icon icon="arrow-repeat" class="mr-1"></b-icon>
                        Fetch Products
                    </b-button>
                    <b-button variant="info" @click="openBatchGenerateModal" style="padding:10px" class="ml-2 glass-btn" size="sm">
                        Batch Generate
                    </b-button>
                </div>
                
                <div class="controls-right">
                    <div class="filter-group">
                        <label class="filter-label">Filter by Category</label>
                        <b-form-select 
                            :options="categoryOptions" 
                            v-model="category_id" 
                            @change="fetch_init(false, true)"
                            style="width: 220px;"
                            size="sm"
                            class="glass-select">
                        </b-form-select>
                    </div>
                    
                    <div class="filter-group">
                        <label class="filter-label">Search</label>
                        <div class="search-wrapper">
                            <b-input 
                                v-model="product_query" 
                                size="sm" 
                                placeholder="Name or SKU..."
                                @keyup.enter="searchProducts"
                                style="width: 200px;"
                                class="glass-input">
                            </b-input>
                            <div v-if="product_query" class="search-clear" @click="clearSearch">✕</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats Bar -->
            <div class="stats-bar glass-stats" v-if="paginatedProducts.length > 0">
                <div class="stats-info">
                    <span class="stats-count">{{ paginatedProducts.length }}</span>
                    <span class="stats-label">products</span>
                    <span class="stats-separator">•</span>
                    <span class="stats-total">{{ totalItemsCount }} total</span>
                    <span class="stats-separator">•</span>
                    <span class="stats-total">Page {{ currentPage }} / {{ totalPages }}</span>
                </div>
                <div class="stats-filters" v-if="category_id || product_query">
                    <span v-if="category_id" class="filter-tag">
                        {{ getCategoryName(category_id) }}
                        <button class="filter-tag-remove" @click="clearCategoryFilter">×</button>
                    </span>
                    <span v-if="product_query" class="filter-tag">
                        "{{ product_query }}"
                        <button class="filter-tag-remove" @click="clearSearch">×</button>
                    </span>
                </div>
            </div>

            <!-- Products Table -->
            <div class="table-container" v-if="paginatedProducts.length > 0" @scroll="handleScroll">
                <table class="editable-table">
                    <thead>
                        <tr>
                            <th style="min-width: 70px;">ID</th>
                            <th style="min-width: 180px;">COLLECTION</th>
                            <th style="min-width: 220px;">PRODUCT NAME</th>
                            <th style="min-width: 160px;">SKU</th>
                            <th style="min-width: 120px;">CATEGORY</th>
                            <!-- HIGHLIGHTED COLUMNS -->
                            <th style="min-width: 230px; background: #fff3e0; border-left: 3px solid #ff9800;">SUB CATEGORY</th>
                            <th style="min-width: 200px; background: #fff3e0; border-left: 3px solid #ff9800;">TYPE</th>
                            <th style="min-width: 200px; background: #fff3e0; border-left: 3px solid #ff9800;">COLOR</th>
                            <th style="min-width: 200px; background: #fff3e0; border-left: 3px solid #ff9800;">SIZE</th>
                            <th style="min-width: 210px; background: #fff3e0; border-left: 3px solid #ff9800;">MATERIAL</th>
                            <th style="min-width: 230px; background: #fff3e0; border-left: 3px solid #ff9800;">FUNCTIONALITY</th>
                            <th style="min-width: 200px; background: #fff3e0; border-left: 3px solid #ff9800;">BED TYPE</th>
                            <th style="min-width: 200px; background: #fff3e0; border-left: 3px solid #ff9800;">STYLE</th>
                            <th style="min-width: 200px; background: #fff3e0; border-left: 3px solid #ff9800;">SHAPE</th>
                            <th style="min-width: 200px;">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, idx) in paginatedProducts" :key="item.sku || idx" :class="{ 'row-editing': pendingSaves.has(`${item.id}_${activeField}`) }">
                            <td class="cell-id">{{ item.id || '-' }}</td>
                            
                            <!-- COLLECTION NAME Field -->
                            <td>
                                <b-input 
                                    v-model="item.collection_name" 
                                    size="sm" 
                                    class="editable-input"
                                    placeholder="Collection name"
                                    @blur="saveField(item, 'collection_name')"
                                    @focus="activeField = 'collection_name'">
                                </b-input>
                            </td>
                            
                            <!-- PRODUCT NAME -->
                            <td>
                                <div class="input-with-suggestion">
                                    <b-input 
                                        v-model="item.product_name" 
                                        size="sm" 
                                        class="editable-input"
                                        @blur="saveField(item, 'product_name')"
                                        @focus="activeField = 'product_name'">
                                    </b-input>
                                    <b-button 
                                        v-if="canGenerateSuggestion(item)"
                                        size="sm" 
                                        variant="outline-info" 
                                        class="suggestion-btn"
                                        @click="generateAndApplyDescription(item)"
                                        title="Generate description from attributes">
                                        <b-icon icon="info-circle" font-scale="0.8"></b-icon>
                                    </b-button>
                                </div>
                            </td>
                            
                            <!-- SKU -->
                            <td>
                                <div class="input-with-suggestion">
                                    <b-input 
                                        v-model="item.sku" 
                                        size="sm" 
                                        class="editable-input"
                                        @blur="saveField(item, 'sku')"
                                        @focus="activeField = 'sku'">
                                    </b-input>
                                    <b-button 
                                        v-if="canGenerateSuggestion(item)"
                                        size="sm" 
                                        variant="outline-info" 
                                        class="suggestion-btn"
                                        @click="generateAndApplySku(item)"
                                        title="Generate SKU from attributes (Format: CAT-TYPE-###-COLOR-MATERIAL-SIZE-SUB)">
                                        <b-icon icon="info-circle" font-scale="0.8"></b-icon>
                                    </b-button>
                                </div>
                            </td>
                            
                            <!-- CATEGORY -->
                            <td>
                                <b-form-select 
                                    v-model="item.category_id" 
                                    :options="categoryOptions.filter(o => o.value !== null)"
                                    size="sm"
                                    class="editable-select"
                                    @change="onCategoryChange(item)">
                                </b-form-select>
                            </td>
                            
                            <!-- SUB CATEGORY - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.subcategory_id" 
                                    :options="getFilteredSubcategoryOptions(item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'subcategory_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- TYPE - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.type_id" 
                                    :options="getFilteredOptions('type', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'type_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- COLOR - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.color_id" 
                                    :options="getFilteredOptions('color', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'color_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- SIZE - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.size_id" 
                                    :options="getFilteredOptions('size', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'size_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- MATERIAL - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.material_id" 
                                    :options="getFilteredOptions('material', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'material_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- FUNCTIONALITY - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.functionality_id" 
                                    :options="getFilteredOptions('functionality', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'functionality_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- BED TYPE - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.bed_type_id" 
                                    :options="getFilteredOptions('bed_type', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'bed_type_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- STYLE - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.style_id" 
                                    :options="getFilteredOptions('style', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'style_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- SHAPE - HIGHLIGHTED -->
                            <td class="highlight-col">
                                <b-form-select 
                                    v-model="item.shape_id" 
                                    :options="getFilteredOptions('shape', item.category_id)"
                                    size="sm"
                                    class="editable-select"
                                    @change="saveField(item, 'shape_id')">
                                </b-form-select>
                            </td>
                            
                            <!-- ACTIONS -->
                            <td class="action-cell">
                                <div class="action-buttons">
                                    <b-button 
                                        size="sm" 
                                        variant="outline-success" 
                                        @click="generateAndApplyBoth(item)"
                                        title="Generate both SKU and Description"
                                        class="generate-both-btn">
                                        <b-icon icon="info-circle"></b-icon>
                                        <span class="btn-text">Generate Both</span>
                                    </b-button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                
                <!-- Pagination - 20 products per page -->
                <div class="pagination-wrapper" v-if="groupedProductsList.length > 0">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="groupedProductsList.length"
                        :per-page="displayPageSize"
                        size="sm"
                        class="mt-3"
                        align="center"
                        @change="onPageChange"
                    ></b-pagination>
                    <div class="text-center text-muted small mb-2">
                        Showing {{ ((currentPage-1)*displayPageSize)+1 }} to {{ Math.min(currentPage*displayPageSize, groupedProductsList.length) }} of {{ groupedProductsList.length }} products
                    </div>
                </div>
                
                <div v-if="isLoadingMore" class="loading-more">
                    <div class="loading-more-spinner"></div>
                    <span>Loading more products...</span>
                </div>
                
                <div v-if="!hasMore && paginatedProducts.length > 0 && !isLoadingMore" class="all-loaded">
                    <div class="end-line"></div>
                    <small class="text-muted">✨ All {{ totalItemsCount }} products loaded ✨</small>
                    <div class="end-line"></div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!items_being_loaded" class="empty-state glass-empty">
                <div class="empty-animation">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="empty-icon">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                </div>
                <h3>No products found</h3>
                <p>Try adjusting your filters or refresh to load products.</p>
                <b-button variant="primary" size="sm" @click="fetch_init(true, true)" class="load-btn glass-btn">
                    Load Products
                </b-button>
            </div>
        </div>
        
        <!-- Batch Generate Modal -->
        <b-modal 
            v-model="showBatchGenerateModal" 
            title="Batch Generate SKU & Description"
            size="lg"
            hide-footer
            centered>
            <div class="batch-generate-modal">
                <div class="modal-body-content">
                    <div class="alert alert-info">
                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                        This will generate SKUs and descriptions for all products in the current view based on their selected attributes.
                    </div>
                    
                    <div class="form-group mb-3">
                        <label class="form-label">What to generate?</label>
                        <b-form-checkbox-group v-model="batchGenerateOptions" class="mt-2">
                            <b-form-checkbox value="sku">Generate SKU</b-form-checkbox>
                            <b-form-checkbox value="description" class="ml-3">Generate Description</b-form-checkbox>
                        </b-form-checkbox-group>
                    </div>
                    
                    <div class="form-group mb-3">
                        <label class="form-label">Generation Mode</label>
                        <b-form-radio-group v-model="batchGenerateMode" class="mt-2">
                            <b-form-radio value="all">All products ({{ groupedProductsList.length }})</b-form-radio>
                            <b-form-radio value="empty_only">Only empty fields</b-form-radio>
                        </b-form-radio-group>
                    </div>
                    
                    <div class="form-group mb-3" v-if="batchGenerateOptions.includes('sku')">
                        <label class="form-label">SKU Format</label>
                        <b-form-select v-model="skuFormat" :options="skuFormatOptions" size="sm"></b-form-select>
                        <small class="text-muted">Preview: {{ getSkuFormatPreview() }}</small>
                    </div>
                    
                    <div class="form-group mb-3" v-if="batchGenerateOptions.includes('description')">
                        <label class="form-label">Description Format</label>
                        <b-form-select v-model="descriptionFormat" :options="descriptionFormatOptions" size="sm"></b-form-select>
                        <small class="text-muted">Preview: COLLECTION COLOR SIZE SUB CATEGORY IN MATERIAL</small>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <b-button variant="secondary" @click="showBatchGenerateModal = false">Cancel</b-button>
                    <b-button variant="primary" @click="executeBatchGenerate" :disabled="batchGenerating">
                        <b-spinner v-if="batchGenerating" small class="mr-1"></b-spinner>
                        {{ batchGenerating ? 'Generating...' : 'Generate & Apply' }}
                    </b-button>
                </div>
            </div>
        </b-modal>
        
        <!-- Suggestion Preview Modal -->
        <b-modal 
            v-model="showSuggestionModal" 
            title="Suggested SKU & Description"
            size="md"
            centered
            @ok="applySuggestion">
            <div class="suggestion-preview-modal" v-if="currentSuggestion">
                <div class="suggestion-section">
                    <label class="font-weight-bold">Original:</label>
                    <div class="original-value mb-2 p-2 bg-light rounded">
                        SKU: {{ currentSuggestion.originalSku || '(empty)' }}<br>
                        Description: {{ currentSuggestion.originalDesc || '(empty)' }}
                    </div>
                    
                    <label class="font-weight-bold text-success mt-3">Suggested:</label>
                    <div class="suggested-value p-2 bg-success-light rounded" :class="{ 'has-change': currentSuggestion.skuChanged }">
                        <div class="d-flex justify-content-between align-items-center">
                            <span>
                                <strong>SKU:</strong> {{ currentSuggestion.suggestedSku }}
                            </span>
                            <b-badge v-if="currentSuggestion.skuChanged" variant="warning">New</b-badge>
                            <b-badge v-else variant="secondary">Unchanged</b-badge>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mt-1">
                            <span>
                                <strong>Description:</strong> {{ currentSuggestion.suggestedDesc }}
                            </span>
                            <b-badge v-if="currentSuggestion.descChanged" variant="warning">New</b-badge>
                            <b-badge v-else variant="secondary">Unchanged</b-badge>
                        </div>
                    </div>
                    
                    <div v-if="currentSuggestion.generationReason" class="mt-3 small text-muted">
                        <b-icon icon="info-circle"></b-icon>
                        {{ currentSuggestion.generationReason }}
                    </div>
                </div>
            </div>
            <template slot="modal-footer">
                <b-button variant="secondary" @click="showSuggestionModal = false">Cancel</b-button>
                <b-button variant="primary" @click="applySuggestion">Apply Suggestion</b-button>
            </template>
        </b-modal>
        
        <!-- Toast Notifications -->
        <div class="toast-notifications">
            <div v-for="toast in toasts" :key="toast.id" :class="['toast-item', toast.type]" @click="removeToast(toast.id)">
                <div class="toast-icon">
                    <svg v-if="toast.type === 'success'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>
                <span>{{ toast.message }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";

export default {
    name: "EditableProductsList",
    data: function() {
        return {
            // Enhanced Loading Animation Properties
            isPageLoading: false,
            loadingProgress: 0,
            loadingMessage: 'Loading Products',
            currentTipIndex: 0,
            loadingInterval: null,
            tipInterval: null,
            msgInterval: null,
            tips: [
                "📦 Fetching product catalog...",
                "🔄 Syncing with inventory database...",
                "✨ Loading product attributes...",
                "📊 Calculating stock levels...",
                "🔍 Preparing editable fields...",
                "💾 Connecting to server...",
                "🎨 Applying latest updates..."
            ],
            
            packet: {},
            items_being_loaded: false,
            isLoadingMore: false,
            
            allItems: [],
            groupedProductsList: [],
            paginatedProducts: [],
            currentOffset: 0,
            pageSize: 100,
            hasMore: true,
            totalItemsCount: 0,
            
            // Pagination properties
            currentPage: 1,
            displayPageSize: 20,
            
            category_id: null,
            product_query: null,
            
            activeField: null,
            toasts: [],
            toastId: 0,
            
            categoryOptions: [{ value: null, text: 'ALL CATEGORIES' }],
            subcategoryOptions: [],
            categoryNames: {},
            
            // Attribute Options Maps - store options per category
            typeOptionsMap: {},
            colorOptionsMap: {},
            sizeOptionsMap: {},
            materialOptionsMap: {},
            functionalityOptionsMap: {},
            bedTypeOptionsMap: {},
            styleOptionsMap: {},
            shapeOptionsMap: {},
            
            // Store color codes fetched from backend
            colorCodesMap: {},
            
            pendingSaves: new Map(),
            saveTimeout: null,
            
            // Auto-generation properties
            showBatchGenerateModal: false,
            showSuggestionModal: false,
            batchGenerating: false,
            batchGenerateOptions: ['sku', 'description'],
            batchGenerateMode: 'empty_only',
            skuFormat: 'standard',
            descriptionFormat: 'standard',
            skuFormatOptions: [
                { value: 'standard', text: 'Standard: CAT-TYPE-###-COLOR-MATERIAL-SIZE-SUB' },
                { value: 'compact', text: 'Compact: CAT-TYPE-###-COLOR-MATERIAL-SIZE-SUB' },
                { value: 'detailed', text: 'Detailed: CAT-TYPE-###-COLOR-MATERIAL-SIZE-SUB' }
            ],
            descriptionFormatOptions: [
                { value: 'standard', text: 'Standard: COLLECTION COLOR SIZE SUB CATEGORY IN MATERIAL' },
                { value: 'detailed', text: 'Detailed: COLLECTION - COLOR - SIZE - SUB CATEGORY - IN - MATERIAL' },
                { value: 'ecommerce', text: 'E-commerce: Collection Color Size Subcategory Material' }
            ],
            currentSuggestion: null,
            pendingSuggestionProduct: null,
            
            // SKU Counter per category (starts from 100)
            skuCounters: {},
            
            // Debounced suggestion update
            suggestionDebounceTimer: null
        };
    },
    computed: {
        filtered_final: function() {
            return this.paginatedProducts;
        },
        currentTip() {
            return this.tips[this.currentTipIndex] || this.tips[0];
        },
        totalPages() {
            return Math.ceil(this.groupedProductsList.length / this.displayPageSize);
        }
    },
    methods: {
        // ============================================
        // PRODUCT GROUPING
        // ============================================
        
        normalizeProductName(name) {
            if (!name) return '';
            let normalized = name.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
            if (normalized.includes('beneli') || normalized.includes('benelli')) return 'benelli';
            return normalized;
        },
        
        groupProductsByName(products) {
            const groups = new Map();
            for (const product of products) {
                const normalized = this.normalizeProductName(product.product_name);
                if (!groups.has(normalized)) {
                    groups.set(normalized, []);
                }
                groups.get(normalized).push(product);
            }
            const result = [];
            for (const group of groups.values()) {
                group.sort((a, b) => (a.id || 0) - (b.id || 0));
                result.push(...group);
            }
            return result;
        },
        
        updateGroupingAndPagination() {
            let filtered = [...this.allItems];
            if (this.category_id) {
                filtered = filtered.filter(item => item.category_id == this.category_id);
            }
            if (this.product_query && this.product_query.trim()) {
                const query = this.product_query.trim().toUpperCase();
                filtered = filtered.filter(item => 
                    (item.product_name && item.product_name.toUpperCase().includes(query)) ||
                    (item.sku && item.sku.toUpperCase().includes(query)) ||
                    (item.collection_name && item.collection_name.toUpperCase().includes(query))
                );
            }
            this.groupedProductsList = this.groupProductsByName(filtered);
            this.totalItemsCount = this.allItems.length;
            this.currentPage = 1;
            this.updatePaginatedList();
        },
        
        updatePaginatedList() {
            const start = (this.currentPage - 1) * this.displayPageSize;
            const end = start + this.displayPageSize;
            this.paginatedProducts = this.groupedProductsList.slice(start, end);
        },
        
        onPageChange(page) {
            this.currentPage = page;
            this.updatePaginatedList();
            const container = this.$el.querySelector('.table-container');
            if (container) container.scrollTop = 0;
        },
        
        // ============================================
        // UPDATED SKU GENERATION METHODS
        // Format: CAT-TYPE-###-COLOR-MATERIAL-SIZE-SUB
        // Example: BA-100-BL-CH-FBB (Bedroom Adult Bunkbed Black Full Chenille)
        // ============================================
        
        canGenerateSuggestion(item) {
            return item && (item.category_id || item.subcategory_id || item.type_id);
        },
        
        getAttributeName(attrMap, id, defaultName = '') {
            if (!id || !attrMap) return defaultName;
            const option = attrMap.find(opt => opt.value === id);
            return option ? option.text : defaultName;
        },
        
        getCategoryName(categoryId) {
            const cat = this.categoryOptions.find(opt => opt.value === categoryId);
            return cat ? cat.text : '';
        },
        
        // 1. Get first letter of Category
        getCategoryInitial(categoryId) {
            if (!categoryId) return 'X';
            const categoryName = this.getCategoryName(categoryId);
            if (!categoryName) return 'X';
            return categoryName.charAt(0).toUpperCase();
        },
        
        // 2. Get first letter of Type
        getTypeInitial(categoryId, typeId) {
            if (!typeId) return 'X';
            const typeName = this.getAttributeName(
                this.getFilteredOptions('type', categoryId), 
                typeId, 
                ''
            );
            if (!typeName) return 'X';
            return typeName.charAt(0).toUpperCase();
        },
        
        // 3. Generate random 3-digit number (starts from 100 per category)
        getRandomThreeDigitNumber(categoryId) {
            if (!this.skuCounters[categoryId]) {
                this.skuCounters[categoryId] = 100;
            }
            const counter = this.skuCounters[categoryId];
            this.skuCounters[categoryId]++;
            return String(counter).padStart(3, '0');
        },
        
        // 4. Get Color Code (2 letters) - Fetched from backend
        getColorCode(categoryId, colorId) {
            if (!colorId) return '00';
            
            // Check if we have the color code in our map
            const catKey = String(categoryId);
            if (this.colorCodesMap[catKey] && this.colorCodesMap[catKey][colorId]) {
                return this.colorCodesMap[catKey][colorId];
            }
            
            // Fallback: try to get from options
            const colorName = this.getAttributeName(
                this.getFilteredOptions('color', categoryId), 
                colorId, 
                ''
            );
            if (!colorName) return '00';
            
            // Generate fallback code from name
            return colorName.substring(0, 2).toUpperCase().replace(/[^A-Z]/g, '');
        },
        
        // 5. Get Material Code (2 letters)
        getMaterialCode(categoryId, materialId) {
            if (!materialId) return '00';
            const materialName = this.getAttributeName(
                this.getFilteredOptions('material', categoryId), 
                materialId, 
                ''
            );
            if (!materialName) return '00';
            return materialName.substring(0, 2).toUpperCase().replace(/[^A-Z]/g, '');
        },
        
        // 6. Get first letter of Size
        getSizeInitial(categoryId, sizeId) {
            if (!sizeId) return '';
            const sizeName = this.getAttributeName(
                this.getFilteredOptions('size', categoryId), 
                sizeId, 
                ''
            );
            if (!sizeName) return '';
            return sizeName.charAt(0).toUpperCase();
        },
        
        // 7. Get initials of Subcategory (each word's first letter)
        getSubcategoryInitials(categoryId, subcategoryId) {
            if (!subcategoryId) return '';
            const subcategoryName = this.getAttributeName(
                this.getFilteredSubcategoryOptions(categoryId), 
                subcategoryId, 
                ''
            );
            if (!subcategoryName) return '';
            
            const words = subcategoryName.split(' ');
            const initials = words
                .map(word => word.charAt(0).toUpperCase())
                .join('');
            
            return initials;
        },
        
        // Main SKU Generation Method
        generateSku(item, format = null) {
            if (!item.category_id) return null;
            
            // Get all components
            const categoryInitial = this.getCategoryInitial(item.category_id);
            const typeInitial = this.getTypeInitial(item.category_id, item.type_id);
            const randomNum = this.getRandomThreeDigitNumber(item.category_id);
            const colorCode = this.getColorCode(item.category_id, item.color_id);
            const materialCode = this.getMaterialCode(item.category_id, item.material_id);
            const sizeInitial = this.getSizeInitial(item.category_id, item.size_id);
            const subcategoryInitials = this.getSubcategoryInitials(item.category_id, item.subcategory_id);
            
            // Build SKU with correct format: CAT-TYPE-###-COLOR-MATERIAL-SIZE-SUB
            let sku = `${categoryInitial}${typeInitial}-${randomNum}-${colorCode}-${materialCode}`;
            
            // Add size if available (no dash between size and subcategory)
            if (sizeInitial) {
                sku += `-${sizeInitial}`;
            }
            
            // Add subcategory initials if available (no dash between size and subcategory)
            if (subcategoryInitials) {
                // If size exists, subcategory comes right after without a dash
                // If no size, subcategory comes after a dash
                if (sizeInitial) {
                    sku += `${subcategoryInitials}`;  // No dash between size and subcategory
                } else {
                    sku += `-${subcategoryInitials}`;
                }
            }
            
            return sku;
        },
        
        // Generate description with correct natural format
        generateDescription(item, format = null) {
            if (!item.category_id) return null;
            
            const formatType = format || this.descriptionFormat;
            const collectionName = item.collection_name || '';
            const categoryName = this.getCategoryName(item.category_id) || '';
            const subcategoryName = this.getAttributeName(this.getFilteredSubcategoryOptions(item.category_id), item.subcategory_id, '');
            const typeName = this.getAttributeName(this.getFilteredOptions('type', item.category_id), item.type_id, '');
            const colorName = this.getAttributeName(this.getFilteredOptions('color', item.category_id), item.color_id, '');
            const materialName = this.getAttributeName(this.getFilteredOptions('material', item.category_id), item.material_id, '');
            const sizeName = this.getAttributeName(this.getFilteredOptions('size', item.category_id), item.size_id, '');
            const styleName = this.getAttributeName(this.getFilteredOptions('style', item.category_id), item.style_id, '');
            
            const parts = [];
            
            switch (formatType) {
                case 'detailed':
                    // Format: COLLECTION COLOR SIZE SUB CATEGORY IN MATERIAL
                    if (collectionName) parts.push(collectionName.toUpperCase());
                    else if (categoryName) parts.push(categoryName.toUpperCase());
                    if (colorName) parts.push(colorName.toUpperCase());
                    if (sizeName) parts.push(sizeName.toUpperCase());
                    if (subcategoryName) parts.push(subcategoryName.toUpperCase());
                    if (materialName) {
                        parts.push('IN');
                        parts.push(materialName.toUpperCase());
                    }
                    return parts.join(' - ');
                    
                case 'ecommerce':
                    // SEO-friendly format: Collection Color Size Subcategory Material
                    if (collectionName) parts.push(collectionName);
                    else if (categoryName) parts.push(categoryName);
                    if (colorName) parts.push(colorName);
                    if (sizeName) parts.push(sizeName);
                    if (subcategoryName) parts.push(subcategoryName);
                    if (materialName) parts.push(materialName);
                    let desc = parts.join(' ');
                    return desc.trim();
                    
                default:
                    // Standard format: COLLECTION COLOR SIZE SUB CATEGORY IN MATERIAL
                    if (collectionName) parts.push(collectionName.toUpperCase());
                    else if (categoryName) parts.push(categoryName.toUpperCase());
                    if (colorName) parts.push(colorName.toUpperCase());
                    if (sizeName) parts.push(sizeName.toUpperCase());
                    if (subcategoryName) parts.push(subcategoryName.toUpperCase());
                    if (materialName) {
                        parts.push('IN');
                        parts.push(materialName.toUpperCase());
                    }
                    return parts.join(' ');
            }
        },

        // Update the generation reason to reflect the new format
        getGenerationReason(item) {
            const reasons = [];
            if (item.collection_name) reasons.push(`Collection: ${item.collection_name}`);
            else if (item.category_id) reasons.push(`Category: ${this.getCategoryName(item.category_id)}`);
            if (item.color_id) reasons.push(`Color: ${this.getAttributeName(this.getFilteredOptions('color', item.category_id), item.color_id)}`);
            if (item.subcategory_id) reasons.push(`Subcategory: ${this.getAttributeName(this.getFilteredSubcategoryOptions(item.category_id), item.subcategory_id)}`);
            if (item.size_id) reasons.push(`Size: ${this.getAttributeName(this.getFilteredOptions('size', item.category_id), item.size_id)}`);
            if (item.material_id) reasons.push(`Material: ${this.getAttributeName(this.getFilteredOptions('material', item.category_id), item.material_id)}`);
            if (item.type_id) reasons.push(`Type: ${this.getAttributeName(this.getFilteredOptions('type', item.category_id), item.type_id)}`);
            if (reasons.length === 0) return "Using default format";
            return `Generated from: ${reasons.join(', ')}`;
        },
        
        generateSuggestion(item) {
            if (!this.canGenerateSuggestion(item)) {
                this.showToast("Please select at least Category or Type first", "warning");
                return null;
            }
            const suggestedSku = this.generateSku(item);
            const suggestedDesc = this.generateDescription(item);
            return {
                suggestedSku: suggestedSku || '',
                suggestedDesc: suggestedDesc || '',
                originalSku: item.sku || '',
                originalDesc: item.product_name || '',
                skuChanged: suggestedSku && suggestedSku !== item.sku,
                descChanged: suggestedDesc && suggestedDesc !== item.product_name,
                generationReason: this.getGenerationReason(item)
            };
        },
        
        generateAndApplySku(item) {
            const newSku = this.generateSku(item);
            if (newSku && newSku !== item.sku) {
                const oldSku = item.sku;
                item.sku = newSku;
                this.saveField(item, 'sku');
                this.showToast(`SKU updated: ${oldSku || 'empty'} → ${newSku}`, "success");
            } else if (newSku === item.sku) {
                this.showToast("SKU unchanged - already matches generated value", "info");
            } else {
                this.showToast("Unable to generate SKU - missing required attributes", "warning");
            }
        },
        
        generateAndApplyDescription(item) {
            const generatedDesc = this.generateDescription(item);
            if (generatedDesc && generatedDesc !== item.product_name) {
                const oldDesc = item.product_name;
                item.product_name = generatedDesc;
                this.saveField(item, 'product_name');
                this.showToast(`Description updated: ${oldDesc || 'empty'} → ${generatedDesc}`, "success");
            } else if (generatedDesc === item.product_name) {
                this.showToast("Description unchanged - already matches generated value", "info");
            } else {
                this.showToast("Unable to generate description - missing required attributes", "warning");
            }
        },
        
        generateAndApplyBoth(item) {
            this.generateAndApplySku(item);
            this.generateAndApplyDescription(item);
        },
        
        applySuggestion() {
            if (this.pendingSuggestionProduct && this.currentSuggestion) {
                if (this.currentSuggestion.skuChanged && this.currentSuggestion.suggestedSku) {
                    this.pendingSuggestionProduct.sku = this.currentSuggestion.suggestedSku;
                    this.saveField(this.pendingSuggestionProduct, 'sku');
                }
                if (this.currentSuggestion.descChanged && this.currentSuggestion.suggestedDesc) {
                    this.pendingSuggestionProduct.product_name = this.currentSuggestion.suggestedDesc;
                    this.saveField(this.pendingSuggestionProduct, 'product_name');
                }
                this.showToast("Suggestion applied successfully", "success");
            }
            this.showSuggestionModal = false;
            this.pendingSuggestionProduct = null;
            this.currentSuggestion = null;
        },
        
        openBatchGenerateModal() {
            this.showBatchGenerateModal = true;
        },
        
        getSkuFormatPreview() {
            return 'BA-100-BL-CH-FBB (Bedroom Adult Black Full Chenille Bunkbed)';
        },
        
        async executeBatchGenerate() {
            if (this.groupedProductsList.length === 0) {
                this.showToast("No products to process", "warning");
                this.showBatchGenerateModal = false;
                return;
            }
            
            this.batchGenerating = true;
            let skuUpdated = 0;
            let descUpdated = 0;
            
            try {
                for (const item of this.groupedProductsList) {
                    const shouldGenerateSku = this.batchGenerateOptions.includes('sku');
                    const shouldGenerateDesc = this.batchGenerateOptions.includes('description');
                    
                    if (shouldGenerateSku) {
                        const shouldUpdate = this.batchGenerateMode === 'all' || !item.sku;
                        if (shouldUpdate) {
                            const newSku = this.generateSku(item);
                            if (newSku && newSku !== item.sku) {
                                item.sku = newSku;
                                skuUpdated++;
                            }
                        }
                    }
                    
                    if (shouldGenerateDesc) {
                        const shouldUpdate = this.batchGenerateMode === 'all' || !item.product_name;
                        if (shouldUpdate) {
                            const generatedDesc = this.generateDescription(item);
                            if (generatedDesc && generatedDesc !== item.product_name) {
                                item.product_name = generatedDesc;
                                descUpdated++;
                            }
                        }
                    }
                }
                
                await this.saveAllPendingChanges();
                
                let message = '';
                if (skuUpdated > 0) message += `${skuUpdated} SKU(s) updated. `;
                if (descUpdated > 0) message += `${descUpdated} description(s) updated.`;
                if (!skuUpdated && !descUpdated) message = 'No changes were needed.';
                this.showToast(message, "success");
                this.updateGroupingAndPagination();
            } catch (error) {
                console.error('Batch generation error:', error);
                this.showToast("Error during batch generation", "error");
            } finally {
                this.batchGenerating = false;
                this.showBatchGenerateModal = false;
            }
        },
        
        async saveAllPendingChanges() {
            const saves = Array.from(this.pendingSaves.values());
            if (saves.length === 0) return;
            this.pendingSaves.clear();
            const productSaves = new Map();
            for (const save of saves) {
                const productKey = save.item.id || save.item.sku;
                if (!productSaves.has(productKey)) {
                    productSaves.set(productKey, { item: save.item, fields: {} });
                }
                productSaves.get(productKey).fields[save.fieldName] = save.value;
            }
            for (const [_, productData] of productSaves) {
                await this.saveProductFields(productData.item, productData.fields);
            }
        },
        
        // ============================================
        // FIXED: saveField Method - Tracks original SKU
        // ============================================
        saveField(item, fieldName) {
            // Store the original SKU if this is the first time we're tracking this item
            if (!item._originalSku && item.sku) {
                // Use Vue.set to make it reactive
                this.$set(item, '_originalSku', item.sku);
            }
            
            const key = `${item.id || item.sku}_${fieldName}`;
            this.pendingSaves.set(key, { item, fieldName, value: item[fieldName] });
            
            clearTimeout(this.saveTimeout);
            this.saveTimeout = setTimeout(() => {
                this.processPendingSaves();
            }, 500);
        },
        
        // ============================================
        // FIXED: processPendingSaves Method
        // ============================================
        async processPendingSaves() {
            const saves = Array.from(this.pendingSaves.values());
            this.pendingSaves.clear();
            const productSaves = new Map();
            for (const save of saves) {
                const productKey = save.item.id || save.item.sku;
                if (!productSaves.has(productKey)) {
                    productSaves.set(productKey, { item: save.item, fields: {} });
                }
                productSaves.get(productKey).fields[save.fieldName] = save.value;
            }
            for (const [_, productData] of productSaves) {
                await this.saveProductFields(productData.item, productData.fields);
            }
        },
        
        // ============================================
        // FIXED: saveProductFields Method - Sends original_sku
        // ============================================
        async saveProductFields(item, fields) {
            let path = "/api/product/update_v1_with_history/";
            
            // CRITICAL: Get the original SKU before any changes
            const originalSku = item._originalSku || item.sku;
            
            let data = {
                id: item.id,
                sku: item.sku,  // This is the NEW SKU
                original_sku: originalSku,  // This is the ORIGINAL SKU for the WHERE clause
                collection_name: item.collection_name || '',
                user_details: {
                    username: this.packet.userDetails.username || "unknown",
                    user_id: this.packet.userDetails.user_id || "unknown"
                }
            };
            
            const fieldMapping = {
                collection_name: 'collection_name',
                product_name: 'product_name',
                sku: 'sku',
                category_id: 'category_id',
                subcategory_id: 'subcategory_id',
                type_id: 'type_id',
                color_id: 'color_id',
                size_id: 'size_id',
                material_id: 'material_id',
                functionality_id: 'functionality_id',
                bed_type_id: 'bed_type_id',
                style_id: 'style_id',
                shape_id: 'shape_id'
            };
            
            for (const [fieldName, value] of Object.entries(fields)) {
                const apiField = fieldMapping[fieldName];
                if (apiField) {
                    data[apiField] = value;
                }
            }
            
            try {
                const response = await new Promise((resolve, reject) => {
                    Requestor.postSyncP(path, data, this, resolve, reject);
                });
                if (response.data.result) {
                    this.showToast(`✓ ${item.sku || item.product_name || 'Product'} updated`, "success");
                    // Update the stored original SKU to the new SKU for future updates
                    if (fields.sku) {
                        item._originalSku = item.sku;
                    }
                } else if (response.data.error) {
                    this.showToast(response.data.error, "error");
                }
            } catch (error) {
                console.error("Save error:", error);
                this.showToast(`✗ Error updating ${item.sku || 'product'}`, "error");
            }
        },
        
        // ============================================
        // LOADING ANIMATION METHODS
        // ============================================
        
        startLoadingAnimation() {
            this.isPageLoading = true;
            this.loadingProgress = 0;
            this.currentTipIndex = 0;
            
            if (this.loadingInterval) clearInterval(this.loadingInterval);
            this.loadingInterval = setInterval(() => {
                if (this.loadingProgress < 90) {
                    this.loadingProgress += Math.random() * 12;
                    if (this.loadingProgress > 90) this.loadingProgress = 90;
                }
            }, 250);
            
            if (this.tipInterval) clearInterval(this.tipInterval);
            this.tipInterval = setInterval(() => {
                if (this.currentTipIndex < this.tips.length - 1) {
                    this.currentTipIndex++;
                } else {
                    this.currentTipIndex = 0;
                }
            }, 2200);
            
            let messages = ['Loading Products', 'Fetching Data', 'Processing Records', 'Updating Inventory', 'Finalizing'];
            let msgIndex = 0;
            if (this.msgInterval) clearInterval(this.msgInterval);
            this.msgInterval = setInterval(() => {
                if (msgIndex < messages.length - 1) {
                    msgIndex++;
                    this.loadingMessage = messages[msgIndex];
                }
            }, 1800);
        },
        
        stopLoadingAnimation() {
            if (this.loadingInterval) {
                clearInterval(this.loadingInterval);
                this.loadingInterval = null;
            }
            if (this.tipInterval) {
                clearInterval(this.tipInterval);
                this.tipInterval = null;
            }
            if (this.msgInterval) {
                clearInterval(this.msgInterval);
                this.msgInterval = null;
            }
            this.loadingProgress = 100;
            setTimeout(() => {
                this.isPageLoading = false;
            }, 400);
        },
        
        showToast(message, type = 'success') {
            const id = ++this.toastId;
            this.toasts.push({ id, message, type });
            setTimeout(() => {
                this.toasts = this.toasts.filter(t => t.id !== id);
            }, 3000);
        },
        
        removeToast(id) {
            this.toasts = this.toasts.filter(t => t.id !== id);
        },
        
        clearSearch() {
            this.product_query = null;
            this.searchProducts();
        },
        
        clearCategoryFilter() {
            this.category_id = null;
            this.fetch_init(false, true);
        },
        
        // ============================================
        // API METHODS
        // ============================================
        
        async fetch_init(first, forceFetch = false) {
            this.startLoadingAnimation();
            if (first) {
                this.category_id = null;
                this.product_query = null;
            }
            this.currentOffset = 0;
            this.hasMore = true;
            this.allItems = [];
            this.groupedProductsList = [];
            this.paginatedProducts = [];
            try {
                await this.fetchProducts(0, this.pageSize, false);
            } finally {
                this.stopLoadingAnimation();
            }
        },
        
     // In your Vue component, update the fetchProducts method:
async fetchProducts(offset = 0, limit = null, append = false) {
    if (this.isLoadingMore && append) return;
    this.isLoadingMore = true;
    if (!append) {
        this.items_being_loaded = true;
    }
    try {
        // CHANGE: Use the new endpoint
        let path = "/api/product/get_products_list_v3_active_only/";
        let data = {
            user_role: this.packet.userDetails.userrole || 'admin',
            offset: offset,
            limit: limit || this.pageSize
        };
        if (this.category_id) data.category_id = this.category_id;
        if (this.product_query) data.product_query = this.product_query;
        
        const response = await new Promise((resolve, reject) => {
            Requestor.getSyncP(path, data, this, resolve, reject);
        });
        
        if (response.data.result) {
            const newItems = response.data.result.map(item => ({
                ...item,
                collection_name: item.collection_name || '',
                edit: false,
                _originalSku: item.sku
            }));
            if (append) {
                this.allItems = [...this.allItems, ...newItems];
            } else {
                this.allItems = newItems;
                this.totalItemsCount = newItems.length;
            }
            this.updateGroupingAndPagination();
            this.hasMore = newItems.length === this.pageSize;
            this.currentOffset = offset + newItems.length;
            if (!append) this.totalItemsCount = this.allItems.length;
        }
    } catch (error) {
        console.error('Error fetching products:', error);
        this.showToast("Error fetching products list", "error");
    } finally {
        this.items_being_loaded = false;
        this.isLoadingMore = false;
    }
},
        
        async loadMore() {
            if (this.isLoadingMore || !this.hasMore) return;
            await this.fetchProducts(this.currentOffset, this.pageSize, true);
        },
        
        searchProducts() {
            this.startLoadingAnimation();
            this.currentOffset = 0;
            this.hasMore = true;
            this.allItems = [];
            this.groupedProductsList = [];
            this.paginatedProducts = [];
            this.fetchProducts(0, this.pageSize, false).finally(() => {
                this.stopLoadingAnimation();
            });
        },
        
        fetchAllOptions() {
            let catPath = "/api/category/get_all_categories/";
            Requestor.getSyncP(catPath, {}, this,
                (response) => {
                    if (!response.data.error && response.data.result) {
                        this.categoryOptions = [
                            { value: null, text: 'ALL CATEGORIES' },
                            ...response.data.result.map(cat => ({
                                value: cat.category_id,
                                text: cat.category_name
                            }))
                        ];
                        response.data.result.forEach(cat => {
                            this.categoryNames[cat.category_id] = cat.category_name;
                        });
                    }
                },
                (error) => console.error("Error loading categories:", error)
            );
            
            let subcatPath = "/api/product/get_subcategories/";
            Requestor.postSyncP(subcatPath, {}, this,
                (response) => {
                    if (!response.data.error && response.data.result) {
                        this.subcategoryOptions = response.data.result.map(subcat => ({
                            value: subcat.id,
                            text: subcat.subcategory_name || subcat.name || subcat.subcategory,
                            category_id: subcat.category_id,
                            code: subcat.code || subcat.subcategory_code
                        }));
                    }
                },
                (error) => console.error("Error loading subcategories:", error)
            );
        },
        
        getFilteredSubcategoryOptions(categoryId) {
            if (!categoryId) return [{ value: null, text: 'Select Category First' }];
            if (!this.subcategoryOptions || !Array.isArray(this.subcategoryOptions) || this.subcategoryOptions.length === 0) {
                return [{ value: null, text: 'Loading subcategories...' }];
            }
            const filtered = this.subcategoryOptions.filter(subcat => Number(subcat.category_id) === Number(categoryId));
            const options = [{ value: null, text: 'Select Subcategory' }];
            filtered.forEach(subcat => options.push({ value: subcat.value, text: subcat.text }));
            return options;
        },
        
        loadAttributeOptionsForCategory(categoryId) {
            if (!categoryId) return;
            const catKey = String(categoryId);
            
            // Load Types
            if (!this.typeOptionsMap[catKey]) {
                this.$set(this.typeOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_types/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(type => ({ 
                                value: type.id, 
                                text: type.type_name,
                                code: type.code || type.type_code 
                            }));
                            this.$set(this.typeOptionsMap, catKey, [{ value: null, text: 'Select Type' }, ...options]);
                        } else {
                            this.$set(this.typeOptionsMap, catKey, [{ value: null, text: 'Select Type' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading types for category ${categoryId}:`, error);
                        this.$set(this.typeOptionsMap, catKey, [{ value: null, text: 'Select Type' }]);
                        this.$forceUpdate();
                    }
                );
            }
            
            // Load Colors - THIS IS WHERE WE FETCH COLOR CODES FROM BACKEND
            if (!this.colorOptionsMap[catKey]) {
                this.$set(this.colorOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                
                // Initialize color codes map for this category
                if (!this.colorCodesMap[catKey]) {
                    this.$set(this.colorCodesMap, catKey, {});
                }
                
                const path = "/api/product/get_category_colors/";
                const data = { category_id: categoryId };
                const that = this;
                
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(color => {
                                // Store the color code in our map for later use
                                const colorCode = color.code || color.color_code || color.color_name.substring(0, 2).toUpperCase();
                                
                                // Store in color codes map
                                if (!that.colorCodesMap[catKey]) {
                                    that.$set(that.colorCodesMap, catKey, {});
                                }
                                that.colorCodesMap[catKey][color.id] = colorCode;
                                
                                return { 
                                    value: color.id, 
                                    text: color.color_name,
                                    code: colorCode
                                };
                            });
                            
                            that.$set(that.colorOptionsMap, catKey, [{ value: null, text: 'Select Color' }, ...options]);
                        } else {
                            that.$set(that.colorOptionsMap, catKey, [{ value: null, text: 'Select Color' }]);
                        }
                        that.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading colors for category ${categoryId}:`, error);
                        that.$set(that.colorOptionsMap, catKey, [{ value: null, text: 'Select Color' }]);
                        that.$forceUpdate();
                    }
                );
            }
            
            // Load Sizes
            if (!this.sizeOptionsMap[catKey]) {
                this.$set(this.sizeOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_sizes/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(size => ({ 
                                value: size.id, 
                                text: size.size_name,
                                code: size.code || size.size_code 
                            }));
                            this.$set(this.sizeOptionsMap, catKey, [{ value: null, text: 'Select Size' }, ...options]);
                        } else {
                            this.$set(this.sizeOptionsMap, catKey, [{ value: null, text: 'Select Size' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading sizes for category ${categoryId}:`, error);
                        this.$set(this.sizeOptionsMap, catKey, [{ value: null, text: 'Select Size' }]);
                        this.$forceUpdate();
                    }
                );
            }
            
            // Load Materials
            if (!this.materialOptionsMap[catKey]) {
                this.$set(this.materialOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_materials/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(material => ({ 
                                value: material.id, 
                                text: material.material_name,
                                code: material.code || material.material_code 
                            }));
                            this.$set(this.materialOptionsMap, catKey, [{ value: null, text: 'Select Material' }, ...options]);
                        } else {
                            this.$set(this.materialOptionsMap, catKey, [{ value: null, text: 'Select Material' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading materials for category ${categoryId}:`, error);
                        this.$set(this.materialOptionsMap, catKey, [{ value: null, text: 'Select Material' }]);
                        this.$forceUpdate();
                    }
                );
            }
            
            // Load Functionalities
            if (!this.functionalityOptionsMap[catKey]) {
                this.$set(this.functionalityOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_functionalities/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(func => ({ 
                                value: func.id, 
                                text: func.functionality_name,
                                code: func.code || func.functionality_code 
                            }));
                            this.$set(this.functionalityOptionsMap, catKey, [{ value: null, text: 'Select Functionality' }, ...options]);
                        } else {
                            this.$set(this.functionalityOptionsMap, catKey, [{ value: null, text: 'Select Functionality' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading functionalities for category ${categoryId}:`, error);
                        this.$set(this.functionalityOptionsMap, catKey, [{ value: null, text: 'Select Functionality' }]);
                        this.$forceUpdate();
                    }
                );
            }
            
            // Load Bed Types
            if (!this.bedTypeOptionsMap[catKey]) {
                this.$set(this.bedTypeOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_bed_types/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(bedType => ({ 
                                value: bedType.id, 
                                text: bedType.style_name,
                                code: bedType.style_code 
                            }));
                            this.$set(this.bedTypeOptionsMap, catKey, [{ value: null, text: 'Select Bed Type' }, ...options]);
                        } else {
                            this.$set(this.bedTypeOptionsMap, catKey, [{ value: null, text: 'Select Bed Type' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading bed types for category ${categoryId}:`, error);
                        this.$set(this.bedTypeOptionsMap, catKey, [{ value: null, text: 'Select Bed Type' }]);
                        this.$forceUpdate();
                    }
                );
            }
            
            // Load Styles
            if (!this.styleOptionsMap[catKey]) {
                this.$set(this.styleOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_styles/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(style => ({ 
                                value: style.id, 
                                text: style.style_name,
                                code: style.style_code 
                            }));
                            this.$set(this.styleOptionsMap, catKey, [{ value: null, text: 'Select Style' }, ...options]);
                        } else {
                            this.$set(this.styleOptionsMap, catKey, [{ value: null, text: 'Select Style' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading styles for category ${categoryId}:`, error);
                        this.$set(this.styleOptionsMap, catKey, [{ value: null, text: 'Select Style' }]);
                        this.$forceUpdate();
                    }
                );
            }
            
            // Load Shapes
            if (!this.shapeOptionsMap[catKey]) {
                this.$set(this.shapeOptionsMap, catKey, [{ value: null, text: 'Loading...' }]);
                const path = "/api/product/get_category_sectional_shapes/";
                const data = { category_id: categoryId };
                Requestor.postSyncP(path, data, this,
                    (response) => {
                        if (!response.data.error && response.data.result) {
                            const options = response.data.result.map(shape => ({ 
                                value: shape.id, 
                                text: shape.shape_name,
                                code: shape.shape_code 
                            }));
                            this.$set(this.shapeOptionsMap, catKey, [{ value: null, text: 'Select Shape' }, ...options]);
                        } else {
                            this.$set(this.shapeOptionsMap, catKey, [{ value: null, text: 'Select Shape' }]);
                        }
                        this.$forceUpdate();
                    },
                    (error) => {
                        console.error(`Error loading shapes for category ${categoryId}:`, error);
                        this.$set(this.shapeOptionsMap, catKey, [{ value: null, text: 'Select Shape' }]);
                        this.$forceUpdate();
                    }
                );
            }
        },
        
        getFilteredOptions(attrKey, categoryId) {
            if (!categoryId) return [{ value: null, text: 'Select Category First' }];
            const catKey = String(categoryId);
            const optionsMap = this[`${attrKey}OptionsMap`];
            if (!optionsMap) return [{ value: null, text: 'Select Option' }];
            const categoryOptions = optionsMap[catKey];
            if (!categoryOptions) {
                this.loadAttributeOptionsForCategory(categoryId);
                return [{ value: null, text: 'Loading...' }];
            }
            return categoryOptions;
        },
        
        // Helper method to get color code directly from the map
        getColorCodeFromBackend(categoryId, colorId) {
            if (!colorId) return '00';
            const catKey = String(categoryId);
            if (this.colorCodesMap[catKey] && this.colorCodesMap[catKey][colorId]) {
                return this.colorCodesMap[catKey][colorId];
            }
            return '00';
        },
        
        onCategoryChange(item) {
            item.subcategory_id = null;
            item.type_id = null;
            item.color_id = null;
            item.size_id = null;
            item.material_id = null;
            item.functionality_id = null;
            item.bed_type_id = null;
            item.style_id = null;
            item.shape_id = null;
            if (item.category_id) {
                this.loadAttributeOptionsForCategory(item.category_id);
            }
            this.saveField(item, 'category_id');
        },
        
        handleScroll(event) {
            const container = event.target;
            const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (scrollBottom < 100 && !this.isLoadingMore && this.hasMore && !this.product_query) {
                this.loadMore();
            }
        }
    },
    created: function() {
        try {
            this.packet = JSON.parse(localStorage.packet || '{}');
            this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
        } catch (error) {
            console.error("Error loading user data:", error);
            this.packet = { userDetails: {} };
        }
        
        this.typeOptionsMap = {};
        this.colorOptionsMap = {};
        this.sizeOptionsMap = {};
        this.materialOptionsMap = {};
        this.functionalityOptionsMap = {};
        this.bedTypeOptionsMap = {};
        this.styleOptionsMap = {};
        this.shapeOptionsMap = {};
        this.colorCodesMap = {};
        
        this.groupedProductsList = [];
        this.paginatedProducts = [];
        
        this.fetchAllOptions();
        this.fetch_init(true);
    },
    mounted() {
        this.$nextTick(() => {
            const container = this.$el.querySelector('.table-container');
            if (container) {
                container.addEventListener('scroll', this.handleScroll);
            }
        });
    },
    beforeDestroy() {
        const container = this.$el.querySelector('.table-container');
        if (container) {
            container.removeEventListener('scroll', this.handleScroll);
        }
        clearTimeout(this.saveTimeout);
        if (this.loadingInterval) clearInterval(this.loadingInterval);
        if (this.tipInterval) clearInterval(this.tipInterval);
        if (this.msgInterval) clearInterval(this.msgInterval);
        if (this.suggestionDebounceTimer) clearTimeout(this.suggestionDebounceTimer);
    }
};
</script>

<style scoped>
/* All existing styles remain the same */
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.editable-products-container.glass-theme {
    position: relative;
    background: transparent !important;
    min-height: 100vh;
    padding: 28px;
}

.editable-products-container.glass-theme::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(10px);
    opacity: 0.4;
    z-index: -2;
    transform: scale(1.1);
}

.products-wrapper {
    background: rgba(255, 255, 255, 0.88);
    backdrop-filter: blur(12px);
    border-radius: 28px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(20px);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-card {
    background: linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98));
    border-radius: 32px;
    padding: 52px 60px;
    text-align: center;
    box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.4);
    animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    max-width: 500px;
    width: 90%;
    border: 1px solid rgba(255,255,255,0.5);
}

@keyframes cardPop {
    from {
        opacity: 0;
        transform: scale(0.85);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.loading-animation {
    margin-bottom: 36px;
}

.loading-ring {
    position: relative;
    width: 110px;
    height: 110px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 4px solid transparent;
    border-radius: 50%;
    animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
    border-top-color: #2c7a4d;
    border-left-color: #2c7a4d;
    animation-duration: 1s;
}

.ring:nth-child(2) {
    border-right-color: #38a169;
    border-bottom-color: #38a169;
    width: 80%;
    height: 80%;
    top: 10%;
    left: 10%;
    animation-duration: 1.2s;
    animation-direction: reverse;
}

.ring:nth-child(3) {
    border-left-color: #48bb78;
    border-bottom-color: #48bb78;
    width: 60%;
    height: 60%;
    top: 20%;
    left: 20%;
    animation-duration: 0.8s;
}

@keyframes ringRotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-icon {
    font-size: 46px;
    animation: iconPulse 1.5s ease-in-out infinite;
    z-index: 1;
}

@keyframes iconPulse {
    0%, 100% { transform: scale(1); opacity: 0.7; }
    50% { transform: scale(1.12); opacity: 1; }
}

.loading-text {
    margin-bottom: 28px;
}

.loading-text h3 {
    color: #1a202c;
    font-size: 1.6rem;
    font-weight: 600;
    margin-bottom: 10px;
    display: inline-block;
}

.loading-dots {
    display: inline-flex;
    margin-left: 10px;
}

.loading-dots .dot {
    width: 8px;
    height: 8px;
    background: #2c7a4d;
    border-radius: 50%;
    margin: 0 3px;
    animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-10px); }
}

.loading-progress {
    width: 100%;
    height: 6px;
    background: #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 24px;
}

.progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #2c7a4d, #48bb78, #68d391, #2c7a4d);
    background-size: 200% 100%;
    animation: progressShine 1.5s linear infinite;
    transition: width 0.3s ease;
    border-radius: 6px;
}

@keyframes progressShine {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.loading-tips {
    margin-top: 24px;
}

.tip-text {
    color: #4a5568;
    font-size: 1rem;
    font-weight: 500;
    animation: tipFade 0.5s ease;
}

@keyframes tipFade {
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.header-controls {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 24px 28px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    flex-wrap: wrap;
    gap: 18px;
    background: rgba(255, 255, 255, 0.5);
}

.controls-left, .controls-right {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
}

.fetch-btn {
    padding: 10px 24px;
    border-radius: 48px;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.2s ease;
    background: linear-gradient(135deg, #2c7a4d, #1e5a38);
    border: none;
}

.fetch-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(44, 122, 77, 0.35);
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.filter-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #4a5568;
}

.glass-select, .glass-input {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
}

.search-wrapper {
    position: relative;
}

.search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #a0aec0;
    font-size: 15px;
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 50%;
    transition: all 0.2s;
}

.search-clear:hover {
    color: #e53e3e;
    background: #f8f9fa;
}

.stats-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 28px;
    background: rgba(248, 250, 252, 0.7);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    font-size: 0.85rem;
    backdrop-filter: blur(4px);
}

.stats-info {
    display: flex;
    align-items: baseline;
    gap: 10px;
}

.stats-count {
    font-weight: 700;
    font-size: 1.2rem;
    color: #2c7a4d;
}

.stats-label {
    color: #718096;
    font-size: 0.8rem;
}

.stats-separator {
    color: #cbd5e0;
}

.stats-total {
    color: #a0aec0;
}

.stats-filters {
    display: flex;
    gap: 10px;
}

.filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px 5px 14px;
    background: rgba(226, 232, 240, 0.8);
    border-radius: 32px;
    font-size: 0.75rem;
    color: #2d3748;
    backdrop-filter: blur(4px);
}

.filter-tag-remove {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    color: #a0aec0;
    padding: 0 3px;
    border-radius: 50%;
}

.filter-tag-remove:hover {
    color: #e53e3e;
}

.table-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: calc(100vh - 260px);
    scroll-behavior: smooth;
}

.editable-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
}

.editable-table th,
.editable-table td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding: 14px 16px;
    vertical-align: middle;
}

.editable-table th {
    background: rgba(247, 250, 252, 0.8);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #4a5568;
    position: sticky;
    top: 0;
    z-index: 10;
    backdrop-filter: blur(8px);
}

.editable-table td {
    white-space: nowrap;
    transition: background 0.2s ease;
}

.highlight-col {
    background-color: #fffef7;
    border-left: 2px solid #ffb74d;
}

.input-with-suggestion {
    display: flex;
    align-items: center;
    gap: 6px;
}

.input-with-suggestion .editable-input {
    flex: 1;
    min-width: 120px;
}

.suggestion-btn {
    padding: 4px 8px;
    border-radius: 8px;
    transition: all 0.2s ease;
    background: rgba(102, 126, 234, 0.1);
    border-color: rgba(102, 126, 234, 0.3);
}

.suggestion-btn:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: scale(1.05);
}

.action-cell {
    white-space: nowrap;
}

.action-buttons {
    display: flex;
    gap: 6px;
    align-items: center;
}

.generate-both-btn {
    padding: 4px 10px;
    font-size: 0.75rem;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border: none;
    color: white;
    transition: all 0.2s;
}

.generate-both-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.generate-both-btn .btn-text {
    margin-left: 4px;
}

.pagination-wrapper {
    padding: 16px 24px;
    background: rgba(255, 255, 255, 0.6);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.suggestion-preview-modal {
    padding: 10px;
}

.original-value {
    background: #f8f9fa;
    border-left: 3px solid #6c757d;
}

.suggested-value {
    background: #e8f5e9;
    border-left: 3px solid #4caf50;
}

.suggested-value.has-change {
    background: #fff3e0;
    border-left-color: #ff9800;
}

.bg-success-light {
    background: rgba(76, 175, 80, 0.1);
}

.batch-generate-modal .modal-body-content {
    padding: 10px;
}

.editable-input {
    font-size: 0.85rem;
    padding: 8px 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    transition: all 0.2s;
    background: rgba(255, 255, 255, 0.95);
}

.editable-input:focus {
    border-color: #2c7a4d;
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 122, 77, 0.15);
}

.editable-select {
    font-size: 0.85rem;
    padding: 0px 12px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.95);
    transition: all 0.2s;
}

.editable-select:focus {
    border-color: #2c7a4d;
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 122, 77, 0.15);
}

.row-editing td {
    background: rgba(253, 230, 138, 0.2);
}

.editable-table tr.saving td {
    background: rgba(198, 246, 213, 0.4);
    transition: background 0.3s;
}

.loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 24px;
    background: rgba(248, 250, 252, 0.7);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    color: #4a5568;
    font-size: 0.85rem;
}

.loading-more-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid rgba(0, 0, 0, 0.1);
    border-top-color: #2c7a4d;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.all-loaded {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 24px;
    background: rgba(248, 250, 252, 0.7);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.end-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #cbd5e0, transparent);
}

.all-loaded small {
    color: #a0aec0;
    font-size: 0.8rem;
}

.empty-state {
    text-align: center;
    padding: 70px 28px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(4px);
}

.empty-animation {
    margin-bottom: 28px;
}

.empty-icon {
    width: 90px;
    height: 90px;
    stroke: #cbd5e0;
    animation: float 3s ease-in-out infinite;
}

@keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
}

.empty-state h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 10px;
}

.empty-state p {
    font-size: 0.9rem;
    color: #a0aec0;
    margin-bottom: 28px;
}

.load-btn {
    border-radius: 40px;
    padding: 10px 28px;
}

.toast-notifications {
    position: fixed;
    bottom: 28px;
    right: 28px;
    z-index: 1100;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.toast-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 24px;
    background: rgba(26, 32, 44, 0.95);
    backdrop-filter: blur(12px);
    color: white;
    border-radius: 60px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    animation: slideInRight 0.3s ease;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255,255,255,0.1);
}

.toast-item.success {
    background: linear-gradient(135deg, rgba(44, 122, 77, 0.95), rgba(30, 90, 56, 0.95));
}

.toast-item.error {
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.95), rgba(167, 29, 42, 0.95));
}

@keyframes slideInRight {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.loading-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.loading-container .loading-ring {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
}

.loading-container .loading-ring div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 64px;
    height: 64px;
    margin: 8px;
    border: 3px solid #2c7a4d;
    border-radius: 50%;
    animation: loading-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #2c7a4d transparent transparent transparent;
}

.loading-container .loading-ring div:nth-child(1) { animation-delay: -0.45s; }
.loading-container .loading-ring div:nth-child(2) { animation-delay: -0.3s; }
.loading-container .loading-ring div:nth-child(3) { animation-delay: -0.15s; }

@keyframes loading-ring {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-container .loading-text {
    margin-top: 20px;
    font-size: 14px;
    color: #2c7a4d;
    font-weight: 500;
    letter-spacing: 1px;
}

.loading-container .loading-dots {
    margin-top: 12px;
    display: flex;
    gap: 6px;
    justify-content: center;
}

.loading-container .loading-dots span {
    width: 6px;
    height: 6px;
    background: #2c7a4d;
    border-radius: 50%;
    animation: loading-bounce 1.4s infinite ease-in-out both;
}

.loading-container .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
.loading-container .loading-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes loading-bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
}

@media (max-width: 768px) {
    .editable-products-container.glass-theme {
        padding: 16px;
    }
    
    .header-controls {
        flex-direction: column;
        align-items: stretch;
        padding: 20px;
    }
    
    .controls-left, .controls-right {
        flex-direction: column;
    }
    
    .filter-group select,
    .filter-group input {
        width: 100% !important;
    }
    
    .stats-bar {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    
    .editable-table th,
    .editable-table td {
        padding: 10px 12px;
    }
    
    .loading-card {
        padding: 36px 28px;
    }
    
    .loading-text h3 {
        font-size: 1.3rem;
    }
    
    .input-with-suggestion {
        min-width: 180px;
    }
    
    .action-buttons {
        flex-direction: column;
    }
    
    .generate-both-btn .btn-text {
        display: none;
    }
}

.table-container::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.table-container::-webkit-scrollbar-track {
    background: rgba(241, 243, 245, 0.5);
    border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}
</style>


// WEBPACK FOOTER //
// src/components/Products/EditProductList.vue